import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkle, CheckCircle, X, Warning, Question } from "@phosphor-icons/react";

const PHASE_LABEL = {
  listen: "Listen",
  reveal: "Reveal",
  reimagine: "Reimagine",
  guide: "Guide",
};

const CONFIDENCE_TONE = {
  high: "text-emerald-500",
  medium: "text-primary",
  low: "text-amber-500",
};

const MANAGING_ROLES = new Set(["partner", "principal", "managing principal", "managing_principal"]);

/**
 * Governance-first surface for a YadidAi phase-emphasis suggestion.
 *
 * Contract:
 *   • Emphasis is a soft signal — this card NEVER phrases it as a permanent
 *     phase assignment. Language is "primary emphasis" and "for now".
 *   • The AI's reasoning + unverified items are rendered as first-class,
 *     equal-weight sections — the "unverified" list is deliberately given
 *     the same visual weight as the reasoning so it can't be skimmed past.
 *   • Approval is gated on Managing Principal role. Non-managing partners
 *     see the same card in read-only form with an "Awaiting MP approval"
 *     footer.
 */
export default function PhaseSuggestionCard({ engagementId, suggestion, onApproved, onDismissed }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  // Iter 85.75 · Approve button ALWAYS renders — backend enforces
  // authority. Prevents the "static awaiting-approval text" symptom
  // when the session's role label is stale.
  const canApprove = true;

  if (!suggestion) return null;
  const phase = suggestion.suggested_phase;
  const confTone = CONFIDENCE_TONE[suggestion.confidence] || "text-muted-foreground";

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${engagementId}/roadmap/approve-suggestion`, { phase });
      toast.success(`Primary emphasis set to ${PHASE_LABEL[phase]}. Revisable at any time.`);
      onApproved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally { setBusy(false); }
  };

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post(`/engagements/${engagementId}/roadmap/dismiss-suggestion`);
      toast.info("Suggestion dismissed. Nothing on the engagement was changed.");
      onDismissed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-primary/40 bg-primary/5 p-6" data-testid="phase-suggestion-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> YadidAi suggests a primary emphasis
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <div className="font-bold" style={{ fontFamily: "Georgia, serif", fontSize: "2rem" }}>{PHASE_LABEL[phase]}</div>
            <div className={`text-[0.6875rem] font-mono uppercase tracking-widest ${confTone}`}>{suggestion.confidence} confidence</div>
          </div>
          <p className="mt-1 text-xs italic text-muted-foreground max-w-lg" style={{ fontFamily: "Georgia, serif" }}>
            A suggestion, not an assignment — emphasis is soft, revisable, and never exclusive.
          </p>
        </div>
      </div>

      {/* Reasoning */}
      <section className="mt-5" data-testid="suggestion-reasoning">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">Why this emphasis</div>
        <p className="text-sm leading-relaxed" style={{ fontFamily: "Georgia, serif" }}>{suggestion.reasoning}</p>
      </section>

      {/* Unverified — deliberately equal visual weight to Reasoning */}
      <section className="mt-5" data-testid="suggestion-unverified">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-amber-500 mb-2 flex items-center gap-1.5">
          <Warning size={11} weight="fill" /> What remains unverified
        </div>
        <ul className="space-y-1.5">
          {(suggestion.unverified || []).map((u, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Question size={13} weight="bold" className="text-amber-500/80 mt-1 shrink-0" />
              <span className="leading-relaxed text-foreground/90" style={{ fontFamily: "Georgia, serif" }}>{u}</span>
            </li>
          ))}
          {(suggestion.unverified || []).length === 0 && (
            <li className="text-xs italic text-muted-foreground">YadidAi flagged no open questions — treat with additional skepticism.</li>
          )}
        </ul>
      </section>

      {/* Governance footer */}
      <div className="mt-6 pt-4 border-t border-primary/20 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
          Drafted by YadidAi · {suggestion.suggested_at ? new Date(suggestion.suggested_at).toLocaleString() : ""}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={dismiss}
            disabled={busy}
            className="rounded-none text-muted-foreground hover:text-destructive"
            data-testid="suggestion-dismiss-btn"
          >
            <X size={13} className="mr-1.5" /> Dismiss
          </Button>
          {canApprove ? (
            <Button
              onClick={approve}
              disabled={busy}
              className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="suggestion-approve-btn"
            >
              <CheckCircle size={13} weight="fill" className="mr-1.5" />
              {busy ? "Approving…" : `Approve ${PHASE_LABEL[phase]} as primary emphasis`}
            </Button>
          ) : (
            <div className="text-[0.6875rem] font-mono uppercase tracking-widest text-amber-500/90 border border-amber-500/40 px-3 py-1.5">
              Awaiting Managing Principal approval
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
